import React from 'react';
import { Device } from '@smart-tv/components/player/player-devices/types';
import getPlaybackDevices from '@smart-tv/utils/kmm/getPlaybackDevices';
import removeCustomerDevices from '@smart-tv/utils/kmm/removeCustomerDevices';
import { usePlayerContext } from '../_contexts/player-context';
import usePlayer from '../use-player';

export const MAX_DEVICES_LIMIT = 5;

interface DevicesText {
  body1: string;
  body2: string;
  headline: string;
}

export enum DevicesScreen {
  Home = 'HOME',
  Edit = 'EDIT'
}

export interface PlayerDevicesContextProps {
  sid?: number;
  mediaId?: number;
  children: React.ReactNode | React.ReactNode[];
  onClose: () => void;
  onRefreshStream: () => void;
}

export interface PlayerDevicesContextValues {
  devices?: Device[];
  handleClose: () => void;
  handleCloseFeedbackDialog: () => void;
  handleEditDevices: () => void;
  handleRemoveDevices: (uids: string[]) => void;
  openFeedbackDialog: boolean;
  selectedScreen: DevicesScreen;
  texts?: DevicesText;
}

export const PlayerDevicesContext = React.createContext<PlayerDevicesContextValues>(
  {} as PlayerDevicesContextValues
);

export const PlayerDevicesConsumer = PlayerDevicesContext.Consumer;

export const PlayerDevicesProvider = (props: PlayerDevicesContextProps): JSX.Element => {
  const { sid, mediaId, onRefreshStream, onClose, children } = props;

  const [texts, setTexts] = React.useState<DevicesText>();

  const [devices, setDevices] = React.useState<Device[]>();

  const [selectedScreen, setSelectedScreen] = React.useState<DevicesScreen>(DevicesScreen.Home);

  const [openFeedbackDialog, setOpenFeedbackDialog] = React.useState(false);

  const { player } = usePlayerContext();
  const { handlePlayerRoutingOnClose } = usePlayer({});

  const handleGetDevices = React.useCallback(async (sid?: number, mediaId?: number) => {
    const data = await getPlaybackDevices({ sid, mediaId });
    const { devices, texts } = data;

    setTexts(texts);

    setDevices(devices);
  }, []);

  const handleRemoveDevices = React.useCallback(
    (uids: string[]) => {
      setDevices((prevState) => prevState && prevState.filter(({ uid }) => !uids.includes(uid)));

      removeCustomerDevices({ uids, sid, mediaId });

      setOpenFeedbackDialog(true);
    },
    [sid, mediaId]
  );

  const handleEditDevices = React.useCallback(() => {
    setSelectedScreen(DevicesScreen.Edit);
  }, []);

  const handleCloseFeedbackDialog = React.useCallback(() => {
    setOpenFeedbackDialog(false);
  }, []);

  const handleClose = React.useCallback(() => {
    if (devices && devices.length < MAX_DEVICES_LIMIT) {
      onRefreshStream();
    } else if (player) {
      handlePlayerRoutingOnClose();
    }
    onClose();
  }, [devices, player, onClose, onRefreshStream, handlePlayerRoutingOnClose]);

  React.useEffect(() => {
    if ((!sid && !mediaId) || devices) return;

    handleGetDevices(sid, mediaId);
  }, [sid, mediaId, devices, handleGetDevices]);

  return (
    <PlayerDevicesContext.Provider
      value={{
        texts,
        devices,
        handleClose,
        selectedScreen,
        handleEditDevices,
        openFeedbackDialog,
        handleRemoveDevices,
        handleCloseFeedbackDialog
      }}
    >
      {children}
    </PlayerDevicesContext.Provider>
  );
};

export default PlayerDevicesContext;
